// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/**
 * Biometric Authentication Utilities (AA5)
 *
 * Wraps @aparajita/capacitor-biometric-auth for native biometric support
 * (Face ID, Touch ID, Android fingerprint/face). All functions are no-op
 * on web platform.
 *
 * Token storage uses @capacitor/preferences which is encrypted on native platforms.
 */

import { isNativePlatform } from './platform';

type BiometricType = 'face' | 'fingerprint' | 'iris' | 'none';

// BiometryType enum values from @aparajita/capacitor-biometric-auth
const BIOMETRY_NONE = 0;
const BIOMETRY_TOUCH_ID = 1;
const BIOMETRY_FACE_ID = 2;
const BIOMETRY_FINGERPRINT = 3;
const BIOMETRY_FACE = 4;
const BIOMETRY_IRIS = 5;

const TOKEN_KEY = 'clubview_biometric_token';
const EXPIRY_KEY = 'clubview_biometric_expiry';
const ENABLED_KEY = 'clubview_biometric_enabled';

/**
 * Check if the device supports and has enrolled biometrics.
 * Returns false on web platform.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!isNativePlatform()) return false;

  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const result = await BiometricAuth.checkBiometry();
    return result.isAvailable;
  } catch {
    return false;
  }
}

/**
 * Get the type of biometric authentication available on the device.
 */
export async function getBiometricType(): Promise<BiometricType> {
  if (!isNativePlatform()) return 'none';

  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const result = await BiometricAuth.checkBiometry();

    switch (result.biometryType) {
      case BIOMETRY_FACE_ID:
      case BIOMETRY_FACE:
        return 'face';
      case BIOMETRY_TOUCH_ID:
      case BIOMETRY_FINGERPRINT:
        return 'fingerprint';
      case BIOMETRY_IRIS:
        return 'iris';
      case BIOMETRY_NONE:
      default:
        return 'none';
    }
  } catch {
    return 'none';
  }
}

/**
 * Trigger the native biometric prompt. Returns true if authentication succeeded.
 */
export async function authenticateWithBiometric(): Promise<boolean> {
  if (!isNativePlatform()) return false;

  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason: 'Sign in to ClubView',
      cancelTitle: 'Use password instead',
      allowDeviceCredential: false,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Store an encrypted auth token in device Keychain/Keystore via Capacitor Preferences.
 * No-op on web.
 */
export async function storeBiometricToken(token: string, expiresIn: number): Promise<void> {
  if (!isNativePlatform()) return;

  try {
    const { Preferences } = await import('@capacitor/preferences');
    const expiryMs = (Date.now() + expiresIn * 1000).toString();
    await Preferences.set({ key: TOKEN_KEY, value: token });
    await Preferences.set({ key: EXPIRY_KEY, value: expiryMs });
  } catch {
    // Silently fail — biometric is optional
  }
}

/**
 * Retrieve the stored biometric token if it exists and hasn't expired.
 * Returns null if not found, expired, or on web platform.
 */
export async function getBiometricToken(): Promise<string | null> {
  if (!isNativePlatform()) return null;

  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value: token } = await Preferences.get({ key: TOKEN_KEY });
    const { value: expiryStr } = await Preferences.get({ key: EXPIRY_KEY });

    if (!token || !expiryStr) return null;

    const expiry = parseInt(expiryStr, 10);
    if (Date.now() > expiry) {
      // Token expired, clean up
      await clearBiometricToken();
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

/**
 * Remove stored biometric token and related preferences.
 * No-op on web.
 */
export async function clearBiometricToken(): Promise<void> {
  if (!isNativePlatform()) return;

  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: TOKEN_KEY });
    await Preferences.remove({ key: EXPIRY_KEY });
    await Preferences.remove({ key: ENABLED_KEY });
  } catch {
    // Silently fail
  }
}

export { ENABLED_KEY, type BiometricType };
